import { ReferralCampaign, ReferralCampaignCode, ReferralCampaignProduct, ReferralPromoProduct } from 'types'

const DISCOUNT_CODE_PATTERN = /^[A-Z0-9][A-Z0-9_-]{2,63}$/

export type ValidationErrors = Record<string, string>

export const emptyReferralCampaignCode = (): ReferralCampaignCode => ({
  code: '',
  enabled: true,
})

export const parseOptionalNumber = (value: string) => {
  const trimmedValue = value.trim()
  if (trimmedValue === '') return undefined
  return Number(trimmedValue)
}

const cleanString = (value: any) => {
  if (typeof value !== 'string') return value
  const trimmedValue = value.trim()
  return trimmedValue ? trimmedValue : undefined
}

const compactObject = <T extends Record<string, any>>(value: T) => {
  return Object.keys(value).reduce((result: Record<string, any>, key) => {
    const entryValue = cleanString(value[key])
    if (entryValue !== undefined && entryValue !== null) result[key] = entryValue
    return result
  }, {}) as Partial<T>
}

const normalizeCode = (value?: string) => value?.trim().toUpperCase() ?? ''

const validateOptionalNumber = (
  errors: ValidationErrors,
  fieldKey: string,
  value: any,
  label: string,
  options?: { integer?: boolean; positive?: boolean }
) => {
  if (value === undefined || value === null || value === '') return
  if (typeof value !== 'number' || Number.isNaN(value)) {
    errors[fieldKey] = `${label} deve essere un numero.`
    return
  }
  if (options?.integer && !Number.isInteger(value)) {
    errors[fieldKey] = `${label} deve essere un numero intero.`
    return
  }
  if (options?.positive && value <= 0) {
    errors[fieldKey] = `${label} deve essere maggiore di zero.`
    return
  }
  if (!options?.positive && value < 0) {
    errors[fieldKey] = `${label} non puo essere negativo.`
  }
}

export const validateReferralCampaignCodes = (codes: ReferralCampaignCode[]) => {
  const errors: ValidationErrors = {}
  const selectedCodes = new Set<string>()
  const duplicateCodes = new Set<string>()

  if (codes.length === 0) {
    errors['code.0.code'] = 'La campagna deve avere almeno un codice.'
    return errors
  }

  codes.forEach((code, index) => {
    const normalizedCode = normalizeCode(code.code)
    const codeFieldKey = `code.${index}.code`

    if (!normalizedCode) {
      errors[codeFieldKey] = 'Codice obbligatorio.'
    } else if (!DISCOUNT_CODE_PATTERN.test(normalizedCode)) {
      errors[codeFieldKey] = 'Usa 3-64 caratteri: lettere, numeri, "_" o "-".'
    } else if (selectedCodes.has(normalizedCode)) {
      duplicateCodes.add(normalizedCode)
    }

    if (normalizedCode) selectedCodes.add(normalizedCode)

    validateOptionalNumber(errors, `code.${index}.maxRedemptions`, code.maxRedemptions, 'Max redemptions', {
      integer: true,
      positive: true,
    })
    validateOptionalNumber(errors, `code.${index}.commissionPercent`, code.commissionPercent, 'Commissione')
  })

  if (duplicateCodes.size > 0) {
    codes.forEach((code, index) => {
      const normalizedCode = normalizeCode(code.code)
      if (duplicateCodes.has(normalizedCode)) {
        errors[`code.${index}.code`] = 'Codice duplicato nella campagna.'
      }
    })
  }

  return errors
}

export const sanitizeReferralCampaignCodes = (codes: ReferralCampaignCode[]) => {
  return codes.map((code) =>
    compactObject({
      code: normalizeCode(code.code),
      commissionPercent: code.commissionPercent,
      creatorEmail: code.creatorEmail,
      creatorName: code.creatorName,
      creatorSlug: code.creatorSlug,
      enabled: code.enabled !== false,
      label: code.label,
      maxRedemptions: code.maxRedemptions,
      metadata: code.metadata,
    }) as ReferralCampaignCode
  )
}

const sanitizePromoProduct = (promoProduct?: ReferralPromoProduct) => {
  if (!promoProduct) return undefined

  return compactObject({
    badgeLabel: promoProduct.badgeLabel,
    ctaLabel: promoProduct.ctaLabel,
    description: promoProduct.description,
    durationDays: promoProduct.durationDays,
    durationLabel: promoProduct.durationLabel,
    id: promoProduct.id,
    key: promoProduct.key,
    months: promoProduct.months,
    order: promoProduct.order,
    price: promoProduct.price,
    storeProductId: promoProduct.storeProductId,
    theme: promoProduct.theme,
    title: promoProduct.title,
  }) as ReferralPromoProduct
}

export const sanitizeReferralCampaignProducts = (products: ReferralCampaignProduct[]) => {
  return products.map((product) =>
    compactObject({
      baseProductRef: product.baseProductRef,
      promoProduct: sanitizePromoProduct(product.promoProduct),
    }) as ReferralCampaignProduct
  )
}

export const buildReferralCampaignCodesUpdatePayload = (
  campaign: ReferralCampaign,
  codes: ReferralCampaignCode[]
) => ({
  ...campaign,
  codes: sanitizeReferralCampaignCodes(codes),
  products: sanitizeReferralCampaignProducts(campaign.products),
})
